import type { MixDecision } from '@sw/engine'
import type { Placement, Vec2 } from '@sw/schema'
import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useAuth } from '../App.js'
import type { AssetSummary, Bundle, PlacementRow, WorldSummary } from '../api/client.js'
import { ApiError, api, tokens } from '../api/client.js'
import { SoundRadar } from '../components/SoundRadar.js'
import type { BroadcasterInfo } from '../session/live-audio.js'
import { LiveAudio } from '../session/live-audio.js'

// The map library is bigger than everything else here put together, and a
// listener out walking never opens it.
const TestDriveMap = lazy(() => import('../components/TestDriveMap.js'))
import { AudioRuntime } from '../audio/runtime.js'
import type { Diagnostics } from '../session/runtime.js'
import { SessionRuntime } from '../session/runtime.js'
import { SessionSocket } from '../session/socket.js'

type Phase = 'loading' | 'ready' | 'starting' | 'listening' | 'error'

function toPlacement(row: PlacementRow, worldId: string): Placement {
  return {
    id: row.id,
    worldId,
    name: row.name,
    anchor: { lat: row.lat, lon: row.lon },
    rotationDeg: row.rotation_deg,
    scale: row.scale,
    locked: row.locked === 1,
  }
}

export function Player(): ReactNode {
  const { worldId = '' } = useParams()
  const { account, setAccount, loading } = useAuth()

  const [phase, setPhase] = useState<Phase>('loading')
  const [world, setWorld] = useState<WorldSummary>()
  const [error, setError] = useState<string>()
  const [progress, setProgress] = useState({ loaded: 0, total: 0 })
  const [mix, setMix] = useState<MixDecision>()
  const [diagnostics, setDiagnostics] = useState<Diagnostics>()
  const [card, setCard] = useState<{ title: string; body: string } | undefined>()
  const [log, setLog] = useState<{ ts: number; text: string }[]>([])
  const [runMode, setRunMode] = useState(false)
  const [testDrive, setTestDrive] = useState(false)
  const [bundle, setBundle] = useState<Bundle>()
  const [broadcaster, setBroadcaster] = useState<BroadcasterInfo>()
  const [broadcasting, setBroadcasting] = useState(false)
  const [talking, setTalking] = useState(false)
  const [caption, setCaption] = useState<string>()
  const [personalTracks, setPersonalTracks] = useState<AssetSummary[]>([])
  const [musicOn, setMusicOn] = useState(false)

  const audioRef = useRef<AudioRuntime>()
  const runtimeRef = useRef<SessionRuntime>()
  const socketRef = useRef<SessionSocket>()
  const liveRef = useRef<LiveAudio>()
  const startedAtRef = useRef(0)

  // Anyone can listen: a poster with a code should not ask for an account.
  useEffect(() => {
    if (loading || account) return
    api
      .continueAsGuest('Listener')
      .then(setAccount)
      .catch(() => setError('Could not start a guest session.'))
  }, [loading, account, setAccount])

  useEffect(() => {
    if (!account) return
    api
      .getWorld(worldId)
      .then((result) => {
        setWorld(result)
        setPhase('ready')
      })
      .catch((problem) => {
        setError(problem instanceof ApiError ? problem.message : 'Could not load that world.')
        setPhase('error')
      })
  }, [account, worldId])

  useEffect(
    () => () => {
      runtimeRef.current?.stop()
      liveRef.current?.close()
      socketRef.current?.close()
      void audioRef.current?.dispose()
    },
    [],
  )

  const appendLog = useCallback((entry: { ts: number; text: string }) => {
    setLog((previous) => [entry, ...previous].slice(0, 200))
  }, [])

  const startListening = async (): Promise<void> => {
    setPhase('starting')
    setError(undefined)
    try {
      // The audio context must be created inside the tap that asked for it.
      const audio = new AudioRuntime()
      await audio.start({ onGlitch: () => appendLog({ ts: Date.now(), text: 'Audio hiccup' }) })
      audioRef.current = audio

      const joined = await api.joinSession(worldId, { displayName: account?.displayName })
      const loaded = await api.bundle(worldId, joined.session.placementId ?? undefined)
      setBundle(loaded)

      audio.setWorld(loaded.definition)
      setProgress({ loaded: 0, total: loaded.assets.length })
      await audio.loadAssets(loaded.assets, (done, total) => setProgress({ loaded: done, total }))

      const socket = new SessionSocket(
        joined.session.id,
        joined.participant.id,
        () => tokens.access,
        {
          onEvent: (event) => {
            if (event.type === 'ruleFired' && event.scope === 'session') {
              appendLog({ ts: Date.now(), text: `Server rule: ${event.ruleId}` })
            }
          },
          onActions: (actions) => runtimeRef.current?.performServerActions(actions),
          onRefused: (message) =>
            appendLog({ ts: Date.now(), text: `Live connection refused: ${message}` }),
          onBroadcaster: (info) => liveRef.current?.handleBroadcaster(info),
          onRtc: (from, payload) => void liveRef.current?.handleSignal(from, payload),
          onBroadcastRefused: (message) => {
            setBroadcasting(false)
            appendLog({ ts: Date.now(), text: message })
          },
        },
      )
      socket.connect()
      socketRef.current = socket

      const runtime = new SessionRuntime(loaded.definition, loaded.placement, audio, socket, {
        onDiagnostics: setDiagnostics,
        onMix: setMix,
        onLog: appendLog,
        onCard: (shown) => {
          setCard(shown)
          window.setTimeout(() => setCard(undefined), shown.durationMs)
        },
        onCaption: (shown) => setCaption(shown?.text),
      })

      runtime.setTranscripts(
        new Map(
          loaded.assets
            .filter((asset) => asset.transcript)
            .map((asset) => [asset.id, asset.transcript as string]),
        ),
      )

      // Start where the world is, so a desk test has somewhere sensible to
      // stand before the first tap on the map.
      runtime.moveTo({ x: 0, y: 0 })
      runtime.start()
      runtimeRef.current = runtime
      startedAtRef.current = Date.now()

      liveRef.current = new LiveAudio(socket, joined.participant.id, {
        onStream: (participantId, stream) => runtime.attachLiveStream(participantId, stream),
        onBroadcasterChanged: (info) => {
          setBroadcaster(info)
          runtime.setBroadcaster(info)
          if (info) appendLog({ ts: Date.now(), text: `${info.name} is speaking` })
        },
        onError: (message) => appendLog({ ts: Date.now(), text: message }),
      })

      if (account && !account.guest) {
        api
          .listAssets('personal')
          .then((result) => setPersonalTracks(result.assets))
          .catch(() => undefined)
      }

      if (loaded.placement) {
        try {
          await runtime.useGps()
        } catch {
          appendLog({ ts: Date.now(), text: 'No location available; using manual positioning.' })
          setTestDrive(true)
        }
      } else {
        setTestDrive(true)
      }

      setPhase('listening')
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : 'Could not start listening.')
      setPhase('error')
    }
  }

  const buyTicket = async (): Promise<void> => {
    try {
      const checkout = await api.startCheckout(worldId)
      window.location.href = checkout.url
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : 'Could not start checkout.')
    }
  }

  if (phase === 'loading' || !world) {
    return <main className="empty">{error ?? 'Loading…'}</main>
  }

  if (phase === 'listening' && runMode) {
    return (
      <RunMode
        startedAt={startedAtRef.current}
        diagnostics={diagnostics}
        onExit={() => setRunMode(false)}
      />
    )
  }

  return (
    <main className="player">
      {phase !== 'listening' ? (
        <StartScreen
          world={world}
          phase={phase}
          error={error}
          progress={progress}
          onStart={startListening}
          onBuy={buyTicket}
        />
      ) : (
        <Listening
          world={world}
          bundle={bundle}
          mix={mix}
          diagnostics={diagnostics}
          log={log}
          testDrive={testDrive}
          onToggleTestDrive={async (on) => {
            setTestDrive(on)
            if (on) runtimeRef.current?.useManualPositioning()
            else await runtimeRef.current?.useGps()
          }}
          onMoveTo={(position) => runtimeRef.current?.moveTo(position)}
          onFace={(heading) => runtimeRef.current?.face(heading)}
          onRunMode={() => setRunMode(true)}
          onBusTrim={(busId, gainDb) => runtimeRef.current?.setBusTrim(busId, gainDb)}
          broadcaster={broadcaster}
          broadcasting={broadcasting}
          talking={talking}
          onToggleBroadcast={async () => {
            const live = liveRef.current
            if (!live) return
            if (broadcasting) {
              live.stopBroadcasting()
              setBroadcasting(false)
              setTalking(false)
              return
            }
            try {
              // Start where the listener is standing, so the voice has a place
              // before the pin has been dragged anywhere.
              await live.startBroadcasting(runtimeRef.current?.currentPosition ?? null)
              setBroadcasting(true)
            } catch {
              appendLog({ ts: Date.now(), text: 'The microphone is not available.' })
            }
          }}
          onTalk={(on) => {
            liveRef.current?.setTalking(on)
            setTalking(on)
          }}
          onMoveVoice={(position) => liveRef.current?.moveTo(position)}
          personalTracks={personalTracks}
          musicOn={musicOn}
          onToggleMusic={async (on) => {
            setMusicOn(on)
            await runtimeRef.current?.setPersonalMusic(
              on ? personalTracks.map((t) => ({ id: t.id, url: t.url, bytes: t.bytes })) : [],
            )
          }}
          onAddTrack={async (file) => {
            const asset = await api.uploadAsset(file, {
              name: file.name,
              mime: file.type,
              kind: 'personal',
            })
            setPersonalTracks((previous) => [asset, ...previous])
          }}
          runtime={runtimeRef}
          worldId={worldId}
        />
      )}

      {caption ? (
        <div className="card-toast" role="status" aria-live="polite" style={{ bottom: '5.5rem' }}>
          <span className="faint">Being said</span>
          <p style={{ margin: '0.2rem 0 0' }}>{caption}</p>
        </div>
      ) : null}

      {card ? (
        <div className="card-toast" role="status">
          <strong>{card.title}</strong>
          {card.body ? <p className="muted" style={{ margin: '0.3rem 0 0' }}>{card.body}</p> : null}
        </div>
      ) : null}
    </main>
  )
}

function StartScreen({
  world,
  phase,
  error,
  progress,
  onStart,
  onBuy,
}: {
  world: WorldSummary
  phase: Phase
  error: string | undefined
  progress: { loaded: number; total: number }
  onStart: () => void
  onBuy: () => void
}): ReactNode {
  const needsTicket = !world.pricing.free && !world.entitled && !world.owned

  return (
    <div style={{ padding: '1.5rem', maxWidth: '32rem', margin: '0 auto' }}>
      <Link to="/" className="faint">
        ← All worlds
      </Link>
      <h1 style={{ marginTop: '0.8rem' }}>{world.name}</h1>
      <p className="muted">{world.description || 'No description yet.'}</p>

      <div className="row tight" style={{ marginBottom: '1rem' }}>
        <span className="pill">{world.rating}</span>
        <span className="pill">{world.instancing === 'shared' ? 'shared world' : 'your own instance'}</span>
        {world.pricing.free ? (
          <span className="pill good">free</span>
        ) : (
          <span className="pill warn">
            {(world.pricing.priceCents / 100).toFixed(2)} {world.pricing.currency.toUpperCase()}
          </span>
        )}
      </div>

      <div className="banner">
        <strong>Headphones, please.</strong>
        <p className="muted" style={{ margin: '0.25rem 0 0' }}>
          {world.headphoneHint === 'openEar'
            ? 'This world is meant for outdoors. Open-ear or bone-conduction headphones let you hear traffic as well as the world.'
            : 'The whole point is direction, and speakers cannot give you that.'}
        </p>
      </div>

      {error ? <div className="banner bad">{error}</div> : null}

      {needsTicket ? (
        <>
          <p className="muted">This world needs a ticket before you can listen.</p>
          <button className="primary big" onClick={onBuy} style={{ width: '100%' }}>
            Buy a ticket · {(world.pricing.priceCents / 100).toFixed(2)}{' '}
            {world.pricing.currency.toUpperCase()}
          </button>
        </>
      ) : (
        <button
          className="primary big"
          onClick={onStart}
          disabled={phase === 'starting' || !world.published}
          style={{ width: '100%' }}
        >
          {phase === 'starting'
            ? progress.total > 0
              ? `Loading sound ${progress.loaded} of ${progress.total}…`
              : 'Starting…'
            : 'Start listening'}
        </button>
      )}

      {!world.published ? (
        <p className="faint" style={{ marginTop: '0.6rem' }}>
          This world has not been published yet.
        </p>
      ) : null}

      {phase === 'starting' && progress.total > 0 ? (
        <div className="meter" style={{ marginTop: '0.8rem' }}>
          <div style={{ width: `${(progress.loaded / progress.total) * 100}%` }} />
        </div>
      ) : null}

      <p className="faint" style={{ marginTop: '1rem' }}>
        Everything is fetched before you set off, so the world keeps working without a signal.
      </p>
    </div>
  )
}

function Listening({
  world,
  bundle,
  mix,
  diagnostics,
  log,
  testDrive,
  onToggleTestDrive,
  onMoveTo,
  onFace,
  onRunMode,
  onBusTrim,
  broadcaster,
  broadcasting,
  talking,
  onToggleBroadcast,
  onTalk,
  onMoveVoice,
  personalTracks,
  musicOn,
  onToggleMusic,
  onAddTrack,
  runtime,
  worldId,
}: {
  world: WorldSummary
  bundle: Bundle | undefined
  mix: MixDecision | undefined
  diagnostics: Diagnostics | undefined
  log: { ts: number; text: string }[]
  testDrive: boolean
  onToggleTestDrive: (on: boolean) => void
  onMoveTo: (position: Vec2) => void
  onFace: (heading: number) => void
  onRunMode: () => void
  onBusTrim: (busId: string, gainDb: number) => void
  broadcaster: BroadcasterInfo | undefined
  broadcasting: boolean
  talking: boolean
  onToggleBroadcast: () => void
  onTalk: (on: boolean) => void
  onMoveVoice: (position: Vec2) => void
  personalTracks: AssetSummary[]
  musicOn: boolean
  onToggleMusic: (on: boolean) => void
  onAddTrack: (file: File) => Promise<void>
  runtime: React.MutableRefObject<SessionRuntime | undefined>
  worldId: string
}): ReactNode {
  const placement = useMemo(
    () => (bundle?.placement ? toPlacement(bundle.placement, world.id) : undefined),
    [bundle, world.id],
  )

  return (
    <div style={{ padding: '1rem', maxWidth: '34rem', margin: '0 auto', width: '100%' }}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h2 style={{ margin: 0 }}>{world.name}</h2>
        <ConnectionPill diagnostics={diagnostics} />
      </div>

      <SoundRadar mix={mix} headingDeg={diagnostics?.headingDeg ?? 0} />

      <div className="row" style={{ justifyContent: 'center', marginBottom: '0.8rem' }}>
        <button className="primary" onClick={onRunMode}>
          Run mode
        </button>
        <button className={testDrive ? 'primary' : ''} onClick={() => onToggleTestDrive(!testDrive)}>
          {testDrive ? 'Test Drive on' : 'Test Drive'}
        </button>
      </div>

      {testDrive && placement ? (
        <Suspense fallback={<div className="card faint">Loading the map…</div>}>
          <TestDriveMap
          placement={placement}
          bundle={bundle}
          position={diagnostics?.filteredPosition}
          headingDeg={diagnostics?.headingDeg ?? 0}
            onMoveTo={broadcasting ? onMoveVoice : onMoveTo}
            onFace={onFace}
          />
        </Suspense>
      ) : null}

      <LiveControls
        broadcaster={broadcaster}
        broadcasting={broadcasting}
        talking={talking}
        testDrive={testDrive}
        onToggleBroadcast={onToggleBroadcast}
        onTalk={onTalk}
      />

      <PersonalMusic
        tracks={personalTracks}
        playing={musicOn}
        onToggle={onToggleMusic}
        onAdd={onAddTrack}
      />

      <Simulator bundle={bundle} runtime={runtime} worldId={worldId} />

      <BusFaders buses={bundle?.definition.buses ?? []} onTrim={onBusTrim} />
      <DiagnosticsPanel diagnostics={diagnostics} mix={mix} />

      <div className="card">
        <h3>What has happened</h3>
        <div className="log">
          {log.length === 0 ? <div>Nothing yet. Walk, or drop yourself somewhere.</div> : null}
          {log.map((entry, index) => (
            <div key={`${entry.ts}-${index}`}>
              {new Date(entry.ts).toLocaleTimeString()} · {entry.text}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function ConnectionPill({ diagnostics }: { diagnostics: Diagnostics | undefined }): ReactNode {
  if (!diagnostics) return null
  if (diagnostics.inSafetyZone) {
    return (
      <span className="pill warn" title="The creator marked this spot as one to keep your ears free">
        <span className="dot" /> quietened
      </span>
    )
  }
  if (diagnostics.accuracyDegraded) {
    return (
      <span className="pill warn" title="Spatial triggers are holding until the fix improves">
        <span className="dot" /> GPS weak
      </span>
    )
  }
  if (!diagnostics.connected) {
    return (
      <span className="pill" title="The authored world keeps running without a connection">
        <span className="dot" /> solo mode
      </span>
    )
  }
  return (
    <span className="pill good">
      <span className="dot" /> live
    </span>
  )
}

/**
 * Speaking into a world from wherever you are.
 *
 * Push to talk rather than an open microphone: a live channel that is always
 * on is a live channel nobody remembers is on. The pin is what makes this more
 * than a phone call — drop it at the five-kilometre mark and your voice rises
 * as the runner reaches you and falls away behind them.
 */
function LiveControls({
  broadcaster,
  broadcasting,
  talking,
  testDrive,
  onToggleBroadcast,
  onTalk,
}: {
  broadcaster: BroadcasterInfo | undefined
  broadcasting: boolean
  talking: boolean
  testDrive: boolean
  onToggleBroadcast: () => void
  onTalk: (on: boolean) => void
}): ReactNode {
  const someoneElse = broadcaster !== undefined && !broadcasting

  return (
    <div className="card">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h3 style={{ margin: 0 }}>Live voice</h3>
        {broadcasting ? (
          <span className={talking ? 'pill bad' : 'pill warn'}>
            <span className="dot" /> {talking ? 'on air' : 'microphone open'}
          </span>
        ) : someoneElse ? (
          <span className="pill good">
            <span className="dot" /> {broadcaster.name}
          </span>
        ) : null}
      </div>

      {someoneElse ? (
        <p className="muted" style={{ margin: '0.3rem 0 0' }}>
          {broadcaster.position
            ? 'They are standing somewhere in this world — walk towards them.'
            : 'They are speaking to the whole world.'}
        </p>
      ) : null}

      <div className="row tight" style={{ marginTop: '0.6rem' }}>
        <button
          className={broadcasting ? '' : 'primary'}
          onClick={onToggleBroadcast}
          disabled={someoneElse}
        >
          {broadcasting ? 'Leave the microphone' : 'Speak into this world'}
        </button>

        {broadcasting ? (
          <button
            className={talking ? 'danger' : 'primary'}
            style={{ flex: 1 }}
            onPointerDown={() => onTalk(true)}
            onPointerUp={() => onTalk(false)}
            onPointerLeave={() => onTalk(false)}
          >
            {talking ? 'Speaking…' : 'Hold to talk'}
          </button>
        ) : null}
      </div>

      {broadcasting ? (
        <p className="faint" style={{ marginTop: '0.5rem' }}>
          {testDrive
            ? 'Tap the map to move your voice to that spot.'
            : 'Turn on Test Drive to place your voice on the map.'}
        </p>
      ) : someoneElse ? null : (
        <p className="faint" style={{ marginTop: '0.5rem' }}>
          One person at a time. Your voice is placed where you stand, and fades with distance like
          anything else here.
        </p>
      )}
    </div>
  )
}

/**
 * The listener's own music, hosted here and mixed into the world.
 *
 * A browser cannot duck a track playing in another app, so the only way a
 * guide's voice can lower the music you are already running is for the music
 * to be inside the world. Native apps will be able to do better; until then
 * this is the honest version rather than a promise that quietly fails.
 */
function PersonalMusic({
  tracks,
  playing,
  onToggle,
  onAdd,
}: {
  tracks: AssetSummary[]
  playing: boolean
  onToggle: (on: boolean) => void
  onAdd: (file: File) => Promise<void>
}): ReactNode {
  const [busy, setBusy] = useState(false)

  return (
    <details className="card">
      <summary style={{ cursor: 'pointer' }}>
        <strong>Your music</strong>{' '}
        <span className="faint">
          {tracks.length === 0 ? 'nothing added' : `${tracks.length} track${tracks.length === 1 ? '' : 's'}`}
        </span>
      </summary>

      <p className="faint" style={{ marginTop: '0.5rem' }}>
        Add your own files and they play on the Music bus, ducking under narration like everything
        else. They stay private to you.
      </p>

      <input
        type="file"
        accept="audio/*"
        multiple
        disabled={busy}
        onChange={async (event) => {
          const files = [...(event.target.files ?? [])]
          setBusy(true)
          for (const file of files) await onAdd(file).catch(() => undefined)
          setBusy(false)
          event.target.value = ''
        }}
      />

      {tracks.length > 0 ? (
        <>
          <div className="stack" style={{ marginTop: '0.6rem' }}>
            {tracks.map((track) => (
              <div key={track.id} className="list-item">
                <span style={{ flex: 1 }}>{track.name}</span>
                <span className="faint">{Math.round(track.bytes / 1024 / 1024)} MB</span>
              </div>
            ))}
          </div>
          <button
            className={playing ? '' : 'primary'}
            style={{ marginTop: '0.6rem', width: '100%' }}
            onClick={() => onToggle(!playing)}
          >
            {playing ? 'Stop my music' : 'Play my music'}
          </button>
        </>
      ) : null}
    </details>
  )
}

/**
 * Driving the world from a desk.
 *
 * Walking a tour to check one cue is a poor way to spend an afternoon, and
 * outdoor testing cannot be repeated exactly. Autopilot walks a route at a
 * chosen pace, the wander slider makes the fix behave like a real one, and a
 * replay re-runs a recorded walk — all through the same seam the sensors use.
 */
function Simulator({
  bundle,
  runtime,
  worldId,
}: {
  bundle: Bundle | undefined
  runtime: React.MutableRefObject<SessionRuntime | undefined>
  worldId: string
}): ReactNode {
  const [routeId, setRouteId] = useState('')
  const [speed, setSpeed] = useState(1.4)
  const [noise, setNoise] = useState(0)
  const [note, setNote] = useState<string>()

  const routes = [
    ...(bundle?.definition.courses ?? []).map((course) => ({
      id: `course:${course.id}`,
      name: `${course.name} (course)`,
      points: course.points,
    })),
    ...(bundle?.definition.emitters ?? [])
      .filter((emitter) => emitter.kind === 'path')
      .map((emitter) => ({
        id: `path:${emitter.id}`,
        name: `${emitter.name} (path)`,
        points: (emitter as { waypoints: Vec2[] }).waypoints,
      })),
  ]

  return (
    <details className="card">
      <summary style={{ cursor: 'pointer' }}>
        <strong>Simulator</strong> <span className="faint">walk it without walking</span>
      </summary>

      <div className="stack" style={{ marginTop: '0.6rem' }}>
        {routes.length === 0 ? (
          <p className="faint">No routes in this world. Import a course or draw a path first.</p>
        ) : (
          <>
            <select value={routeId} onChange={(event) => setRouteId(event.target.value)}>
              <option value="">a route to walk…</option>
              {routes.map((route) => (
                <option key={route.id} value={route.id}>
                  {route.name}
                </option>
              ))}
            </select>

            <div className="field">
              <label htmlFor="sim-speed">
                {speed.toFixed(1)} m/s — {speed < 2 ? 'walking' : speed < 4 ? 'jogging' : 'running'}
              </label>
              <input
                id="sim-speed"
                type="range"
                min={0.5}
                max={8}
                step={0.1}
                value={speed}
                onChange={(event) => setSpeed(Number(event.target.value))}
              />
            </div>

            <div className="row tight">
              <button
                className="primary"
                disabled={!routeId}
                onClick={() => {
                  const route = routes.find((candidate) => candidate.id === routeId)
                  if (route) runtime.current?.followRoute(route.points, speed)
                }}
              >
                Walk it
              </button>
              <button className="ghost" onClick={() => runtime.current?.stopAutopilot()}>
                Stop
              </button>
            </div>
          </>
        )}

        <div className="field">
          <label htmlFor="sim-noise">
            {noise === 0 ? 'A perfectly steady fix' : `Wander by ${noise} m, as a real fix does`}
          </label>
          <input
            id="sim-noise"
            type="range"
            min={0}
            max={25}
            value={noise}
            onChange={(event) => {
              const next = Number(event.target.value)
              setNoise(next)
              runtime.current?.setSimulatedNoise(next)
            }}
          />
        </div>

        <button
          onClick={async () => {
            try {
              const live = await api.liveSessions(worldId)
              const previous = live.sessions.find(
                (session) => session.id !== undefined,
              )
              if (!previous) {
                setNote('No session to replay yet.')
                return
              }
              const trace = await api.trace(previous.id)
              if (trace.poses.length === 0) {
                setNote('That session recorded no positions.')
                return
              }
              runtime.current?.replayTrace(
                trace.poses.map((pose) => ({
                  t: pose.t,
                  position: pose.position,
                  headingDeg: pose.headingDeg ?? undefined,
                  speedMps: pose.speedMps ?? undefined,
                  accuracyM: pose.accuracyM ?? undefined,
                  source: 'replay' as const,
                })),
              )
              setNote(`Replaying ${trace.poses.length} positions.`)
            } catch {
              setNote('That trace could not be read.')
            }
          }}
        >
          Replay the last walk
        </button>
        {note ? <p className="faint">{note}</p> : null}
      </div>
    </details>
  )
}

function BusFaders({
  buses,
  onTrim,
}: {
  buses: { id: string; name: string }[]
  onTrim: (busId: string, gainDb: number) => void
}): ReactNode {
  const [values, setValues] = useState<Record<string, number>>({})
  if (buses.length === 0) return null

  return (
    <div className="card">
      <h3>Mix</h3>
      <div className="stack">
        {buses.map((bus) => {
          const value = values[bus.id] ?? 1
          return (
            <div key={bus.id}>
              <label htmlFor={`bus-${bus.id}`}>
                {bus.name} · {Math.round(value * 100)}%
              </label>
              <input
                id={`bus-${bus.id}`}
                type="range"
                min={0}
                max={1.5}
                step={0.01}
                value={value}
                onChange={(event) => {
                  const next = Number(event.target.value)
                  setValues((previous) => ({ ...previous, [bus.id]: next }))
                  // The slider is a proportion; the mix works in decibels.
                  onTrim(bus.id, next <= 0 ? -80 : 20 * Math.log10(next))
                }}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}

function DiagnosticsPanel({
  diagnostics,
  mix,
}: {
  diagnostics: Diagnostics | undefined
  mix: MixDecision | undefined
}): ReactNode {
  if (!diagnostics) return null
  const audible = mix?.voices.filter((voice) => voice.audible) ?? []

  return (
    <details className="card">
      <summary style={{ cursor: 'pointer' }}>
        <strong>Diagnostics</strong>{' '}
        <span className="faint">
          {audible.length} audible · {diagnostics.positionMode}
        </span>
      </summary>
      <div className="scroll-x" style={{ marginTop: '0.6rem' }}>
        <table className="tabular">
          <tbody>
            <tr>
              <th>Position</th>
              <td className="mono">
                {diagnostics.filteredPosition
                  ? `${diagnostics.filteredPosition.x.toFixed(1)}, ${diagnostics.filteredPosition.y.toFixed(1)} m`
                  : 'unknown'}
              </td>
            </tr>
            <tr>
              <th>Accuracy</th>
              <td className="mono">
                {diagnostics.accuracyM === undefined ? '—' : `${diagnostics.accuracyM.toFixed(0)} m`}
                {diagnostics.accuracyDegraded ? ' (holding triggers)' : ''}
              </td>
            </tr>
            <tr>
              <th>Heading</th>
              <td className="mono">
                {Math.round(diagnostics.headingDeg)}° from {diagnostics.headingSource}
              </td>
            </tr>
            <tr>
              <th>Speed</th>
              <td className="mono">{diagnostics.speedMps.toFixed(1)} m/s</td>
            </tr>
            <tr>
              <th>Voices</th>
              <td className="mono">
                {diagnostics.activeVoices} playing · {diagnostics.culledVoices} silent
              </td>
            </tr>
            <tr>
              <th>Safety zone</th>
              <td className="mono">
                {diagnostics.inSafetyZone ? 'yes — everything ducked' : 'no'}
              </td>
            </tr>
            <tr>
              <th>Rejected fixes</th>
              <td className="mono">{diagnostics.rejectedFixes}</td>
            </tr>
            <tr>
              <th>Clock offset</th>
              <td className="mono">{Math.round(diagnostics.clockOffsetMs)} ms</td>
            </tr>
          </tbody>
        </table>
      </div>

      {audible.length > 0 ? (
        <div className="scroll-x" style={{ marginTop: '0.6rem' }}>
          <table className="tabular">
            <thead>
              <tr>
                <th>Sound</th>
                <th>Bearing</th>
                <th>Distance</th>
                <th>Level</th>
              </tr>
            </thead>
            <tbody>
              {audible.map((voice) => (
                <tr key={voice.emitterId}>
                  <td>{voice.emitterId}</td>
                  <td className="mono">{Math.round(voice.azimuthDeg)}°</td>
                  <td className="mono">{voice.distanceM.toFixed(0)} m</td>
                  <td className="mono">{(voice.gain * 100).toFixed(0)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </details>
  )
}

/**
 * The screen for a phone in a pocket.
 *
 * A browser suspends timers and location the moment the screen locks, which
 * would quietly freeze the world mid-walk. Until the native apps arrive the
 * honest answer is to keep the screen on and make it cost almost nothing to
 * do so: near-black, no motion, one enormous control.
 */
function RunMode({
  startedAt,
  diagnostics,
  onExit,
}: {
  startedAt: number
  diagnostics: Diagnostics | undefined
  onExit: () => void
}): ReactNode {
  const [elapsed, setElapsed] = useState(0)

  useEffect(() => {
    const timer = window.setInterval(() => setElapsed(Date.now() - startedAt), 500)
    return () => window.clearInterval(timer)
  }, [startedAt])

  useEffect(() => {
    let lock: WakeLockSentinel | undefined
    const request = async (): Promise<void> => {
      try {
        lock = await navigator.wakeLock?.request('screen')
      } catch {
        // Some browsers refuse; the warning below covers it.
      }
    }
    void request()

    const reacquire = (): void => {
      if (document.visibilityState === 'visible') void request()
    }
    document.addEventListener('visibilitychange', reacquire)

    return () => {
      document.removeEventListener('visibilitychange', reacquire)
      void lock?.release()
    }
  }, [])

  const minutes = Math.floor(elapsed / 60_000)
  const seconds = Math.floor((elapsed % 60_000) / 1000)

  return (
    <div className="run-mode">
      <div className="elapsed">
        {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
      </div>
      <p className="note">
        Screen stays awake so the world keeps running. Pocket it screen-outwards, or use an armband.
      </p>
      {diagnostics?.accuracyDegraded ? <span className="pill warn">GPS weak</span> : null}
      <button className="big" onClick={onExit}>
        Back to the world
      </button>
    </div>
  )
}
